import { UsuariosService, formatDate } from '@/app/core';
import { Usuario } from '@/app/models';
import { ButtonComponent, UserAvatarComponent } from '@/app/shared';
import {
    COMPETENCIA_MAX_LENGTH,
    DEFAULT_COMPETENCIA,
    UpdateMiPerfilDto,
    resolveCompetencia,
} from '@/app/shared/types/user-profile.types';
import { CommonModule } from '@angular/common';
import { Component, ElementRef, ViewChild, inject, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { CardModule } from 'primeng/card';
import { InputTextModule } from 'primeng/inputtext';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { DividerModule } from 'primeng/divider';
import { TagModule } from 'primeng/tag';
import { ToastModule } from 'primeng/toast';
import { MessageService } from 'primeng/api';

@Component({
    selector: 'app-user-profile',
    standalone: true,
    imports: [
        CommonModule,
        FormsModule,
        CardModule,
        InputTextModule,
        ProgressSpinnerModule,
        DividerModule,
        TagModule,
        ToastModule,
        ButtonComponent,
        UserAvatarComponent,
    ],
    templateUrl: './user-profile.component.html',
    styleUrl: './user-profile.component.scss',
    providers: [MessageService],
})
export class UserProfileComponent implements OnInit {
    private readonly usuariosService = inject(UsuariosService);
    private readonly messageService = inject(MessageService);

    @ViewChild('avatarFileInput', { static: false })
    private readonly avatarFileInput?: ElementRef<HTMLInputElement>;

    usuario: Usuario | null = null;
    loading = true;
    editMode = false;
    saving = false;
    uploadingAvatar = false;

    // Datos editables del formulario base.
    nombre = '';
    apellido = '';
    correo = '';
    competencia = '';

    readonly defaultCompetencia = DEFAULT_COMPETENCIA;
    readonly competenciaMaxLength = COMPETENCIA_MAX_LENGTH;
    private static readonly AVATAR_MAX_BYTES = 5 * 1024 * 1024;

    ngOnInit() {
        this.loadUserProfile();
    }

    loadUserProfile() {
        this.loading = true;
        this.usuariosService.getCurrentUser().subscribe({
            next: (user) => {
                this.usuario = user;
                this.resetForm();
                this.loading = false;
            },
            error: (error) => {
                console.error('Error cargando perfil:', error);
                this.messageService.add({
                    severity: 'error',
                    summary: 'Error',
                    detail: 'Error al cargar el perfil de usuario',
                });
                this.loading = false;
            },
        });
    }

    private resetForm() {
        if (!this.usuario) return;
        this.nombre = this.usuario.nombre || '';
        this.apellido = this.usuario.apellido || '';
        this.correo = this.usuario.correo || '';
        this.competencia = this.usuario.competencia || '';
    }

    enableEditMode() {
        this.editMode = true;
        this.resetForm();
    }

    cancelEdit() {
        this.editMode = false;
        this.resetForm();
    }

    saveProfile() {
        if (!this.usuario) return;
        if (!this.isFormValid) return;
        this.saving = true;

        const previous = this.usuario;
        const payload: UpdateMiPerfilDto = {};

        const nombre = this.nombre.trim();
        if (nombre !== (previous.nombre ?? '').trim()) payload.nombre = nombre;

        const apellido = this.apellido.trim();
        if (apellido !== (previous.apellido ?? '').trim()) {
            payload.apellido = apellido.length ? apellido : null;
        }

        const correo = this.correo.trim().toLowerCase();
        if (correo !== (previous.correo ?? '').trim().toLowerCase()) payload.correo = correo;

        const competencia = this.competencia.trim();
        if (competencia !== (previous.competencia ?? '').trim()) {
            payload.competencia = competencia.length ? competencia : null;
        }

        if (!Object.keys(payload).length) {
            this.saving = false;
            this.editMode = false;
            return;
        }

        this.usuariosService.updateMiPerfil(payload).subscribe({
            next: (updated) => {
                this.usuario = { ...previous, ...updated };
                this.editMode = false;
                this.resetForm();
                this.saving = false;
                this.messageService.add({
                    severity: 'success',
                    summary: 'Éxito',
                    detail: 'Perfil actualizado correctamente',
                });
            },
            error: (error) => {
                console.error('Error actualizando perfil:', error);
                this.saving = false;
                this.messageService.add({
                    severity: 'error',
                    summary: 'Error',
                    detail: this.extractErrorMessage(error, 'Error al actualizar el perfil'),
                });
            },
        });
    }

    private extractErrorMessage(error: unknown, fallback: string): string {
        if (typeof error === 'object' && error !== null) {
            const errObj = error as { error?: { message?: string | string[] }; message?: string };
            const inner = errObj.error?.message ?? errObj.message;
            if (Array.isArray(inner)) return inner.join(' • ');
            if (typeof inner === 'string' && inner.length) return inner;
        }
        return fallback;
    }

    onAvatarPickRequested() {
        if (this.uploadingAvatar) return;
        this.avatarFileInput?.nativeElement.click();
    }

    onAvatarFileSelected(event: Event) {
        const input = event.target as HTMLInputElement | null;
        const file = input?.files?.[0];
        if (input) input.value = '';
        if (!file) return;

        if (!file.type.startsWith('image/')) {
            this.messageService.add({
                severity: 'warn',
                summary: 'Formato inválido',
                detail: 'Seleccioná una imagen (jpg, png, webp).',
            });
            return;
        }

        if (file.size > UserProfileComponent.AVATAR_MAX_BYTES) {
            this.messageService.add({
                severity: 'warn',
                summary: 'Imagen demasiado grande',
                detail: 'El máximo es 5 MB.',
            });
            return;
        }

        this.uploadingAvatar = true;
        this.usuariosService.uploadMyAvatar(file, file.name).subscribe({
            next: (updated) => {
                this.usuario = { ...(this.usuario ?? {} as Usuario), ...updated };
                this.uploadingAvatar = false;
                this.messageService.add({
                    severity: 'success',
                    summary: 'Foto actualizada',
                    detail: 'Tu foto de perfil se actualizó correctamente.',
                });
            },
            error: (error) => {
                console.error('Error subiendo avatar:', error);
                this.uploadingAvatar = false;
                this.messageService.add({
                    severity: 'error',
                    summary: 'Error',
                    detail: 'No pudimos subir la foto. Intentá nuevamente.',
                });
            },
        });
    }

    get isFormValid(): boolean {
        return (
            this.nombre.trim().length > 0 &&
            this.apellido.trim().length > 0 &&
            this.correo.trim().length > 0 &&
            this.isValidEmail(this.correo.trim()) &&
            this.competencia.trim().length <= COMPETENCIA_MAX_LENGTH
        );
    }

    private isValidEmail(email: string): boolean {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
    }

    get competenciaDisplay(): string {
        return resolveCompetencia(this.usuario);
    }

    /* Obtiene sistemas asociados al usuario en orden */
    get userSystems(): string[] {
        if (!this.usuario?.grupos) return [];
        const systemsSet = new Set<string>();

        this.usuario.grupos.forEach((grupo: any) => {
            if (grupo.sistema?.nombre) {
                systemsSet.add(grupo.sistema.nombre);
            }
        });

        return Array.from(systemsSet).sort();
    }

    /* Obtiene sistema y perfiles a los que el usuario está vinculado */
    get userPerfiles(): { sistema: string; perfiles: string[] }[] {
        if (!this.usuario?.grupos) return [];

        const map = new Map<string, Set<string>>();

        this.usuario.grupos.forEach((g: any) => {
            if (!g?.perfil || !g?.sistema) return;

            const sistemaNombre = g.sistema.nombre;
            const perfilLabel = `${g.perfil.nombre}: ${g.perfil.descripcion}`;

            if (!map.has(sistemaNombre)) {
                map.set(sistemaNombre, new Set());
            }

            map.get(sistemaNombre)!.add(perfilLabel);
        });

        return Array.from(map.entries())
            .sort(([a], [b]) => a.localeCompare(b))
            .map(([sistema, perfiles]) => ({
                sistema,
                perfiles: Array.from(perfiles).sort(),
            }));
    }

    /* Helper para fecha de registro */
    get userDateRegister() {
        if (!this.usuario?.createdAt) return 'Sin fecha';
        return formatDate(this.usuario.createdAt);
    }

    /* Helper para fecha de actualización */
    get userDateUpdated() {
        if (!this.usuario?.updatedAt) return 'Sin fecha';
        return formatDate(this.usuario.updatedAt);
    }
}
